import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'auditLog'
const PAGE_SIZE = 50

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

export interface AuditFacets {
	users: { _id: string; name?: string; email?: string }[]
	entityTypes: string[]
	actions: string[]
}

export interface AuditFilters {
	entityType?: string[]
	action?: string[]
	userId?: string[]
	since?: string | null
	before?: string | null
}

interface PageState {
	entries: any[]
	total: number
	loadedCount: number
	isLoading: boolean
	facets: AuditFacets
	filters: AuditFilters
}

const EMPTY_FACETS: AuditFacets = { users: [], entityTypes: [], actions: [] }

const initialState = {
	byEvaluation: {} as Record<string, PageState>,
	error: null as string | null,
}

export const fetchAuditLogPage = createAsyncThunk(
	`${SLICE_NAME}/fetchPage`,
	async (
		{
			evalId,
			offset = 0,
			limit = PAGE_SIZE,
			append = false,
			filters,
		}: {
			evalId: string
			offset?: number
			limit?: number
			append?: boolean
			filters?: AuditFilters
		},
		{ getState, rejectWithValue },
	) => {
		try {
			const params: Record<string, string> = {
				limit: String(limit),
				offset: String(offset),
			}
			if (filters?.entityType?.length) params.entityType = filters.entityType.join(',')
			if (filters?.action?.length) params.action = filters.action.join(',')
			if (filters?.userId?.length) params.userId = filters.userId.join(',')
			if (filters?.since) params.since = filters.since
			if (filters?.before) params.before = filters.before

			const qs = new URLSearchParams(params).toString()
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/audit-log?${qs}`,
				prepConfig(getState),
			)
			return { evalId, append, filters: filters ?? {}, ...data }
		} catch {
			return rejectWithValue('Failed to load audit log')
		}
	},
)

const auditLogSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		resetAuditLog: (state, action: { payload: string }) => {
			delete state.byEvaluation[action.payload]
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAuditLogPage.pending, (state, action) => {
				const { evalId } = action.meta.arg
				const existing = state.byEvaluation[evalId]
				state.byEvaluation[evalId] = existing
					? { ...existing, isLoading: true }
					: {
							entries: [],
							total: 0,
							loadedCount: 0,
							isLoading: true,
							facets: EMPTY_FACETS,
							filters: {},
						}
			})
			.addCase(fetchAuditLogPage.fulfilled, (state, action) => {
				const { evalId, append, entries, total, facets, filters } = action.payload
				const existing = state.byEvaluation[evalId] || {
					entries: [],
					total: 0,
					loadedCount: 0,
					isLoading: false,
					facets: EMPTY_FACETS,
					filters: {},
				}
				const merged = append ? [...existing.entries, ...entries] : entries
				state.byEvaluation[evalId] = {
					entries: merged,
					total,
					loadedCount: merged.length,
					isLoading: false,
					facets: facets ?? existing.facets ?? EMPTY_FACETS,
					filters: filters ?? {},
				}
			})
			.addCase(fetchAuditLogPage.rejected, (state, action) => {
				const { evalId } = action.meta.arg
				const existing = state.byEvaluation[evalId]
				if (existing) existing.isLoading = false
			})
	},
})

export const { resetAuditLog } = auditLogSlice.actions
export const PAGE_SIZE_DEFAULT = PAGE_SIZE
export default auditLogSlice
